/**
 * Kalshi UFC fight props (#848) — bout admission + grouping, as a pure pass
 * over one sport bucket. `marketStore.getUfcFightProps` supplies the store's
 * own keying (`gameKeyV2`), side parsing and book builder, so a fight prop is
 * filed under EXACTLY the game key its bout's KXUFCFIGHT row renders under.
 *
 * Admission (docs/261008KalshiUfcFightProps.md §Bout matching), mirroring
 * Hydra Core's `deriveFightPropCanonicals`:
 *  1. The game's bout = the KXUFCFIGHT legs filed under this game key: one
 *     Kalshi event code, filed under no other game key, two distinct outcome
 *     codes on opposite sides that reassemble the event code's pair
 *     (`ALL` + `DUN` = `ALLDUN`). Otherwise every prop naming the bout is
 *     withheld (ambiguous or incomplete bout).
 *  2. A prop row is rendered only when it carries the producer's canonical
 *     key, that key is this game's key AND its event code is the bout's.
 *     Core publishes a row it refused with NO key; such a row — or any row
 *     naming this bout by event code alone — is withheld, never rendered.
 *  3. Its `class` / `stat` / `seriesTicker`, when present, must agree with
 *     the series token; the leg must parse under that series' closed suffix
 *     grammar, a sided leg's fighter must be exactly one of the bout's two
 *     codes, and every producer term on the wire (`fighter`, `method`,
 *     `round`, `line`) must equal the ticker-derived one; `fighter` (a
 *     key-pair surname) must equal the canonical away/home code on the leg
 *     fighter's own board side. The UFC edge may drop those terms; the
 *     ticker suffix carries the same facts.
 * Titles are never read.
 */
import type { HydraMarket } from '@/clients/HydraClient';
import type { UfcFightPropGroup, UfcFightPropRow, UfcFightProps, VenueBook } from '@/views/ValueDashboard/types';
import {
  UFC_METHOD_ORDER,
  parseUfcFightPropTicker,
  resolveUfcFightPropLeg,
  ufcCanonicalSides,
  ufcEventPair,
  ufcFightEventCode,
  ufcFightPropTermsAgree,
  ufcFightPropWireAgrees,
  type UfcFightPropLeg,
  type UfcFightPropTicker,
} from './fightProps';

export interface UfcFightLegIdentity {
  /** The game key the store files this KXUFCFIGHT leg under; null when the
   *  store refuses to file it (doubleheader-style ambiguity). */
  key: string | null;
  /** The leg's outcome code (ticker suffix, e.g. `DUN`). */
  code: string;
  side: 'away' | 'home';
}

export interface UfcFightPropSelectionInput {
  gameKey: string;
  markets: Iterable<HydraMarket>;
  /** Identity of a KXUFCFIGHT leg as the board files it; null if unparseable. */
  fightLegOf: (m: HydraMarket) => UfcFightLegIdentity | null;
  /** The producer's canonical game key for a prop row, in the store's v2
   *  form: a string when stamped, null when stamped but ambiguous, undefined
   *  when the row carries no canonical key at all. */
  propKeyOf: (m: HydraMarket) => string | null | undefined;
  bookOf: (m: HydraMarket) => VenueBook;
}

const legRound = (leg: UfcFightPropLeg): number =>
  leg.family === 'rounds' ? leg.round : leg.family === 'round-of-victory' ? (leg.round ?? 99) : 0;
const legMethodRank = (leg: UfcFightPropLeg): number =>
  leg.family === 'method-of-victory' || leg.family === 'method-of-finish'
    ? UFC_METHOD_ORDER.indexOf(leg.method)
    : 0;

const GROUP_ORDER = ['rounds', 'mov-away', 'mov-home', 'mov-none', 'mof', 'vic-away', 'vic-home', 'vic-none', 'distance'] as const;

function groupIdOf(leg: UfcFightPropLeg, side: 'away' | 'home' | null): (typeof GROUP_ORDER)[number] {
  switch (leg.family) {
    case 'rounds': return 'rounds';
    case 'distance': return 'distance';
    case 'method-of-finish': return 'mof';
    case 'method-of-victory': return side === 'away' ? 'mov-away' : side === 'home' ? 'mov-home' : 'mov-none';
    case 'round-of-victory': return side === 'away' ? 'vic-away' : side === 'home' ? 'vic-home' : 'vic-none';
  }
}

export function selectUfcFightProps(input: UfcFightPropSelectionInput): UfcFightProps | null {
  const { gameKey } = input;
  // event code → every game key a KXUFCFIGHT leg with that code is filed under
  const fightKeysByEvent = new Map<string, Set<string | null>>();
  const boutEvents = new Set<string>();
  const boutSides = new Map<string, 'away' | 'home' | 'conflict'>();
  const props: Array<{ m: HydraMarket; t: UfcFightPropTicker }> = [];

  for (const m of input.markets) {
    if (m.source !== 'kalshi') continue;
    const ev = ufcFightEventCode(m.ticker);
    if (ev) {
      const leg = input.fightLegOf(m);
      if (!leg) continue;
      let keys = fightKeysByEvent.get(ev);
      if (!keys) { keys = new Set(); fightKeysByEvent.set(ev, keys); }
      keys.add(leg.key);
      if (leg.key === gameKey) {
        boutEvents.add(ev);
        const code = leg.code.toUpperCase();
        // A code seen on both sides is no side at all.
        const prev = boutSides.get(code);
        boutSides.set(code, prev && prev !== leg.side ? 'conflict' : leg.side);
      }
      continue;
    }
    const t = parseUfcFightPropTicker(m.ticker);
    if (t) props.push({ m, t });
  }

  if (boutEvents.size === 0) return null;
  const boutEvent = boutEvents.size === 1 ? [...boutEvents][0]! : null;
  const boutKeys = boutEvent ? fightKeysByEvent.get(boutEvent) : undefined;
  const sideEntries = [...boutSides.entries()].filter(
    (e): e is [string, 'away' | 'home'] => e[1] === 'away' || e[1] === 'home',
  );
  const pair = boutEvent ? ufcEventPair(boutEvent) : '';
  // Two distinct codes on opposite sides that reassemble the event's pair.
  const boutComplete =
    !!boutEvent && !!boutKeys && boutKeys.size === 1 && boutKeys.has(gameKey)
    && boutSides.size === 2 && sideEntries.length === 2
    && sideEntries[0]![1] !== sideEntries[1]![1]
    && (sideEntries[0]![0] + sideEntries[1]![0] === pair || sideEntries[1]![0] + sideEntries[0]![0] === pair);
  const fighterCodes = boutComplete ? sideEntries.map(([c]) => c) : [];
  const sideOfCode = new Map<string, 'away' | 'home'>(boutComplete ? sideEntries : []);

  const rows = new Map<string, UfcFightPropRow[]>();
  const withheld: string[] = [];
  const tickers = new Set<string>();

  for (const { m, t } of props) {
    const pk = input.propKeyOf(m);
    const keyNamesBout = typeof pk === 'string' && pk === gameKey;
    const eventNamesBout = boutEvents.has(t.eventCode);
    // Neither identity points at this game: another bout's prop — not ours to
    // render or to report.
    if (!keyNamesBout && !eventNamesBout) continue;

    const refuse = (): void => { withheld.push(m.ticker); };
    // Unkeyed (Core refused it, or no producer), ambiguous, or keyed to
    // another game: withheld.
    if (!keyNamesBout) { refuse(); continue; }
    if (!boutComplete || t.eventCode !== boutEvent) { refuse(); continue; }
    if (!ufcFightPropWireAgrees(t, m)) { refuse(); continue; }
    const leg = resolveUfcFightPropLeg(t, fighterCodes);
    if (!leg) { refuse(); continue; }
    if (!ufcFightPropTermsAgree(leg, m, { sideOfCode, canonical: ufcCanonicalSides(m) })) { refuse(); continue; }
    const fighterCode = 'fighterCode' in leg ? leg.fighterCode : null;
    const fighterSide = fighterCode ? sideOfCode.get(fighterCode) ?? null : null;
    if (fighterCode && !fighterSide) { refuse(); continue; }
    const id = groupIdOf(leg, fighterSide);
    const list = rows.get(id) ?? [];
    list.push({ ticker: m.ticker, leg, fighterSide, kalshi: input.bookOf(m) });
    rows.set(id, list);
    tickers.add(m.ticker);
  }

  const groups: UfcFightPropGroup[] = [];
  for (const id of GROUP_ORDER) {
    const list = rows.get(id);
    if (!list || list.length === 0) continue;
    list.sort((a, b) =>
      legRound(a.leg) - legRound(b.leg)
      || legMethodRank(a.leg) - legMethodRank(b.leg)
      || a.ticker.localeCompare(b.ticker));
    groups.push({ id, family: list[0]!.leg.family, fighterSide: list[0]!.fighterSide, rows: list });
  }
  withheld.sort();
  if (groups.length === 0 && withheld.length === 0) return null;
  return { groups, tickers, withheld };
}
